import { icon } from '@carvana/ds-styles';
import cx from 'classnames';
import { Leaf } from 'lucide-react';
import { appAssetsCdnUrl } from '~/constants/environment';
import useGetConsumerHref from '~/hooks/common/selectors/useGetConsumerHref';
import { nativeSrpPayload } from '~/native/common/utils';
import generateSearchLinkFilter from '~/utilities/generateSearchFilterLink';
import optimizeImage from '~/utilities/optimizeImage';
import {
  CardContainer,
  CardCta,
  CardSubtitle,
  CardTitle,
  CardTitleContainer
} from './Cards.styles';
import styles from './FuelEfficientCard.module.css';
import { GradientOverlay } from './FuelEfficientCard.styles';

type VehicleCardProps = {
  className?: string;
  isDesktopMedium?: boolean;
};

function ToyotaRav4Hybrid(props: VehicleCardProps) {
  return (
    <div
      className={cx(
        props.className,
        'relative flex flex-col shadow-md h-[73.3px] w-[221px] gap-y-[2px] rounded-[6px] bg-canvas-default px-[12.28px] py-[10px]'
      )}
    >
      <div className="font-[inter] text-[14px] font-semibold leading-[120%] text-text-strong">
        Toyota RAV4
        <br />
        Hybrid
      </div>
      <div className="font-[inter] text-[10.7px] font-semibold leading-[15.3px] text-text-default">
        $25,090
      </div>
      <Leaf
        aria-hidden="true"
        color={icon.strong}
        size={43}
        className="absolute right-5xl top-[-13px] rounded-full bg-decorative-ev p-[7px]"
      />
      <img
        src={optimizeImage(
          `${appAssetsCdnUrl}/images/home/fuel-efficient/toyota-rav4.png`,
          {
            quality: 90,
            format: 'auto'
          }
        )}
        alt=""
        className="absolute bottom-[-17px] right-[-42px] h-[95px]"
        loading="lazy"
      />
    </div>
  );
}

function FordMustangMachE(props: VehicleCardProps) {
  return (
    <img
      src={`${appAssetsCdnUrl}/images/home/fuel-efficient/ford-mustang-mache.png`}
      alt=""
      className={cx(
        props.className,
        'absolute top-0 w-[158px] rounded-sm shadow-md'
      )}
      loading="lazy"
    />
  );
}

function MitsubishiOutlander(props: VehicleCardProps) {
  return (
    <img
      src={`${appAssetsCdnUrl}/images/home/fuel-efficient/mitsubishi-outlander.png`}
      alt=""
      className={cx(
        props.className,
        'absolute top-0 w-[135px] rounded-xs shadow-md'
      )}
      loading="lazy"
    />
  );
}

function NissanLeaf(props: VehicleCardProps) {
  return (
    <img
      src={`${appAssetsCdnUrl}/images/home/fuel-efficient/nissan-leaf.png`}
      alt=""
      className={cx(
        props.className,
        'absolute top-0 w-[107px] rounded-xs shadow-md'
      )}
      loading="lazy"
    />
  );
}

const FUEL_EFFICIENT_SEARCH_LINK = generateSearchLinkFilter({
  filters: { mpg: { min: 40 } }
});

export default function FuelEfficientCard() {
  const getHref = useGetConsumerHref();

  return (
    <CardContainer
      className="bg-canvas-overlay-strong"
      data-analytics-id="Shop Fuel Efficient Cars"
      data-testid="fuel-efficient-card"
      data-native-payload={nativeSrpPayload}
      href={getHref(FUEL_EFFICIENT_SEARCH_LINK)}
    >
      <CardTitleContainer>
        <CardTitle inverse>Go the distance</CardTitle>
        <CardSubtitle inverse>
          We offer a wide variety of vehicles with 40 MPG/MPGe or greater.
        </CardSubtitle>
      </CardTitleContainer>
      <div
        aria-hidden="true"
        className="relative flex h-[180px] items-end justify-center"
      >
        <div className="relative mb-[30px]">
          <NissanLeaf className={cx(styles.nissanLeaf, 'absolute')} />
          <MitsubishiOutlander
            className={cx(styles.mitsubishiOutlander, 'absolute')}
          />
          <FordMustangMachE
            className={cx(styles.fordMustangMachE, 'absolute')}
          />
          <ToyotaRav4Hybrid className={styles.toyotaRav4Hybrid} />
        </div>
        <GradientOverlay />
      </div>
      <CardCta data-testid="shop-fuel-efficient" inverse>
        Shop Fuel Efficient
      </CardCta>
    </CardContainer>
  );
}
